import { useCallback } from 'react';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import {
  isDriveTimePolygon, isIndividualRadius, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  AGS_REPORT_MAX_RADIUS_IN_MILES, isPolygonWithinUS, isProximityCenterWithinUS, isProximityValidForReportExport,
} from '~/exportReport/exportReportDataValidation.helpers';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';

export const useExportReportValidation = () => {
  const [t] = useTranslation();
  const latLngLookup = useLatLngSpreadsheetData();

  const validateReportMultiPolygon = useCallback((multiPolygon: MultiPolygon) => {
    const polygonWithoutHoles: MultiPolygon = multiPolygon.map(polygon => ({
      path: polygon.path,
      holes: [],
    }));

    if (!isPolygonWithinUS(polygonWithoutHoles)) {
      return {
        isDisabled: true,
        reason: t('exportReport.unavailableOutsideUS'),
      };
    }

    return {
      isDisabled: false,
      reason: '',
    };
  }, [t]);

  const validateReportProximity = useCallback((proximity: Proximity, spreadsheetRowId?: SpreadsheetRowId) => {
    let center: { lat: number; lng: number } | undefined;

    if (isDriveTimePolygon(proximity)) {
      center = {
        lat: proximity.data.lat,
        lng: proximity.data.lng,
      };
    }
    else if (isIndividualRadius(proximity)) {
      center = {
        lat: proximity.data.data.lat,
        lng: proximity.data.data.lng,
      };
    }

    if (spreadsheetRowId) {
      const spreadsheetData = latLngLookup.getRow(spreadsheetRowId);

      if (spreadsheetData) {
        center = {
          lat: spreadsheetData.lat,
          lng: spreadsheetData.lng,
        };
      }
    }

    if (center && !isProximityCenterWithinUS(center)) {
      return {
        isDisabled: true,
        reason: t('exportReport.noSupportOutsideUS.radii'),
      };
    }

    if (!isProximityValidForReportExport(proximity, spreadsheetRowId)) {
      return {
        isDisabled: true,
        reason: t('Summary unavailable over {{miles}} miles', {
          miles: AGS_REPORT_MAX_RADIUS_IN_MILES,
        }),
      };
    }

    return {
      isDisabled: false,
      reason: '',
    };
  }, [t, latLngLookup]);

  return {
    validateReportProximity,
    validateReportMultiPolygon,
  };
};
